"use client";

import { useProgressNavigation } from "@/components/progress-bar";

import { useUser } from "@/contexts/user-provider";

import { parseError } from "@/lib/parse-error";
import { toast } from "@/lib/use-toast";

import { setSelectedTenant } from "@/api/auth/login";

/**
 * Move the session to another tenant, and only then navigate.
 *
 * Both places that offer a tenant switch — the profile menu and the command bar
 * — used to call `setSelectedTenant` and navigate regardless of what it
 * returned. `requestData` swallows errors by default, so a refused switch
 * looked exactly like an accepted one: the app navigated to a tenant the
 * session had not moved to, the route's own auth check bounced the person to
 * the login screen, and nothing anywhere said why.
 *
 * That matters most for enterprise staff, whose claim lists brands they may not
 * yet hold an account in.
 */
export function useTenantSwitch() {
  const { currentUser } = useUser();
  const { navigateWithProgress } = useProgressNavigation();

  return async (
    subDomain: string,
    destination: () => Promise<string>,
  ): Promise<boolean> => {
    if (!currentUser?.firebaseUid) return false;

    try {
      // `catchError: false`: the answer is the point. A refusal here is a
      // sentence worth showing, not a null to navigate past.
      await setSelectedTenant(currentUser.firebaseUid, subDomain, false);
    } catch (error) {
      toast({
        title: "Could not switch",
        description: await parseError(
          error,
          "Could not switch to that clinic.",
        ),
        variant: "error",
      });
      return false;
    }

    // Resolved only once the session has moved, so it can read the new tenant.
    navigateWithProgress(await destination(), false, {
      bypassUnsavedChangesGuard: true,
    });
    return true;
  };
}
